// Canonical class identity resolver.
//
// PROBLEM: Historical registrations (`class_payments.class_id`, interest,
// waiting rows) are keyed under a mix of legacy slugs (e.g. "lego-robotics")
// and database UUIDs (e.g. "bb111111-...-000000000002"). UI code that reads
// enrollment always uses ONE of those, so counts collapse to 0 whenever the
// stored key doesn't match the lookup key.
//
// FIX: Every equivalent id belongs to one identity group. Enrollment
// primitives compare via `classIdMatches`, which consults this registry.
//
// This is a TEMPORARY backwards-compatibility layer. The long-term plan is
// to normalize every historical row to a single canonical id (see planned
// maintenance migration) and remove the alias tables. Do NOT build new
// business logic on top of this resolver — always store the canonical id.

import { LEGACY_CLASS_ALIASES } from "@/lib/classAliasTable";

/** Every id in a Set is treated as equivalent. */
const groups: Set<Set<string>> = new Set();
const byId = new Map<string, Set<string>>();

function normalize(id: string | null | undefined): string {
  return String(id ?? "").trim();
}

function dbPrefixVariants(id: string): string[] {
  if (!id) return [];
  if (id.startsWith("db:")) {
    const bare = id.slice(3).trim();
    return bare ? [id, bare] : [id];
  }
  return [id, `db:${id}`];
}

function expandDbPrefixVariants(ids: Iterable<string>): string[] {
  return Array.from(new Set(Array.from(ids).flatMap(dbPrefixVariants)));
}

/** Declare two or more ids as the same class. Idempotent. */
export function registerAliases(...ids: Array<string | null | undefined>) {
  const clean = Array.from(new Set(ids.map(normalize).filter(Boolean)));
  if (clean.length < 2) {
    for (const id of clean) {
      if (!byId.has(id)) {
        const g = new Set<string>([id]);
        groups.add(g);
        byId.set(id, g);
      }
    }
    return;
  }
  // Merge any existing groups that overlap with `clean`.
  const merged = new Set<string>(clean);
  for (const id of clean) {
    const existing = byId.get(id);
    if (existing) {
      for (const v of existing) merged.add(v);
      groups.delete(existing);
    }
  }
  groups.add(merged);
  for (const id of merged) byId.set(id, merged);
}

/** All ids equivalent to `id` (including `id` itself). */
export function getClassAliases(id: string | null | undefined): string[] {
  const key = normalize(id);
  if (!key) return [];
  for (const variant of dbPrefixVariants(key)) {
    const g = byId.get(variant);
    if (g) {
      registerAliases(...dbPrefixVariants(key), ...expandDbPrefixVariants(g));
      return Array.from(byId.get(key) ?? g);
    }
  }
  // Fold in legacy alias table one-shot.
  const bareKey = key.startsWith("db:") ? key.slice(3) : key;
  const legacy = LEGACY_CLASS_ALIASES[bareKey];
  if (legacy && legacy !== key) {
    registerAliases(...dbPrefixVariants(key), ...dbPrefixVariants(legacy));
    return Array.from(byId.get(key) ?? new Set(dbPrefixVariants(key)));
  }
  return Array.from(new Set(dbPrefixVariants(key)));
}

/** True iff `a` and `b` refer to the same class. */
export function classIdMatches(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const x = normalize(a);
  const y = normalize(b);
  if (!x || !y) return false;
  if (x === y) return true;
  if (getClassAliases(x).includes(y)) return true;
  if (getClassAliases(y).includes(x)) return true;
  // Legacy alias fallback.
  const bareX = x.startsWith("db:") ? x.slice(3) : x;
  const bareY = y.startsWith("db:") ? y.slice(3) : y;
  if ((LEGACY_CLASS_ALIASES[bareX] ?? bareX) === (LEGACY_CLASS_ALIASES[bareY] ?? bareY)) return true;
  return false;
}

/**
 * Seed groups we KNOW are equivalent from static data. These come from the
 * `classes` DB table (slug ↔ uuid) captured at the time of the enrollment
 * regression fix. If the DB adds new rows, `hydrateClassIdentityFromDb`
 * merges them at runtime.
 */
const SEED: Array<[string, string]> = [
  ["all-about-farming", "5359fc38-5036-4684-9794-4a103ea70555"],
  ["pokemon-club", "128e5477-cc97-4509-a320-62cb6e95d31b"],
  ["chess-club", "1dd3fc22-c4b0-4f1a-b7a7-3705045e386d"],
  ["mommy-me-crochet", "d9cbe2a8-89eb-4aa8-bfab-6463a36045ec"],
  ["hooved-and-hens", "8e5ba64c-dcb7-41a4-9675-acb04f478619"],
  ["beginners-basketball", "bb111111-1111-4111-8111-000000000001"],
  ["lego-robotics", "bb111111-1111-4111-8111-000000000002"],
  ["beginners-mechanics", "bb111111-1111-4111-8111-000000000003"],
  ["biology-lake-mead", "bb111111-1111-4111-8111-000000000004"],
  ["dam-good-cruisers-e-bike-club", "c250baa9-284d-4f06-9e73-f8870d74fa0f"],
];

for (const [slug, uuid] of SEED) {
  registerAliases(slug, `db:${slug}`, uuid, `db:${uuid}`);
}
for (const [oldSlug, newSlug] of Object.entries(LEGACY_CLASS_ALIASES)) {
  registerAliases(oldSlug, newSlug);
}

/** Merge live DB slug↔id pairs into the registry. Safe to call repeatedly. */
export async function hydrateClassIdentityFromDb(
  supabase: { from: (t: string) => any },
): Promise<void> {
  try {
    const { data } = await supabase.from("classes").select("id, slug");
    for (const row of (data ?? []) as Array<{ id: string; slug: string | null }>) {
      if (row.id && row.slug) registerAliases(row.slug, `db:${row.slug}`, row.id, `db:${row.id}`);
    }
  } catch {
    /* non-fatal — seed already covers current drift */
  }
}

/** TEST-ONLY: wipe registry. */
export function __resetClassIdentityForTests() {
  groups.clear();
  byId.clear();
}
